import React, { useCallback, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { observer } from "mobx-react-lite";

import "./SinglePagePdfViewer.scss";
import Button from "../button/Button";
import useThemService from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface SinglePagePdfViewerProps {
  pdfUrl: string;
  pageNumber?: number;
}

export default observer(function SinglePagePdfViewer(props: SinglePagePdfViewerProps) {
  const { pdfUrl, pageNumber } = props;

  const { themeClass } = useThemService();

  const [numPages, setNumPages] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(pageNumber || 1);
  const [documentIsLoaded, setDocumentIsLoaded] = useState(false);
  const [pageIsRendered, setPageIsRendered] = useState(false);

  const onDocumentLoadSuccess = useCallback(({ numPages }: any) => {
    setNumPages(numPages);
    setDocumentIsLoaded(true);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error("Error loading pdf file: ", error);
  }, []);

  const onPageRenderSuccess = useCallback(() => {
    setPageIsRendered(true);
  }, []);

  const changePage = useCallback((offset: number) => {
    setCurrentPageNumber((prevPageNumber) => prevPageNumber + offset);
    setPageIsRendered(false);
  }, []);

  const previousPage = useCallback(() => {
    changePage(-1);
  }, [changePage]);

  const nextPage = useCallback(() => {
    changePage(1);
  }, [changePage]);

  return (
    <div className="single-page-pdf-viewer">
      <Document
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={<LoadingSpinner />}
      >
        {!pageIsRendered && <LoadingSpinner />}
        <Page
          pageNumber={currentPageNumber}
          scale={1.2}
          className="pdf-page"
          onRenderSuccess={onPageRenderSuccess}
        />
      </Document>
      {documentIsLoaded && (
        <>
          <p className="current-page">
            {translationService.translate("general.currentPage", {
              currentPage: currentPageNumber,
              totalPages: numPages,
            })}
          </p>
          <div className="previous-next-buttons">
            <Button
              id="previous"
              onClick={previousPage}
              disabled={currentPageNumber <= 1}
              textKey="general.previous"
              className={themeClass(["btn-small"])}
            />
            <Button
              id="next"
              onClick={nextPage}
              disabled={currentPageNumber >= numPages}
              textKey="general.next"
              className={themeClass(["btn-small"])}
            />
          </div>
        </>
      )}
    </div>
  );
});
